import { mirrorChartMargin } from "src/components/charts/chartRtl";
import { formatNumberDigits, getNumberLocale } from "src/utils/numberFormat";

type PlatformTrendAxisTickProps = {
  x?: number;
  y?: number;
  payload?: { value: number };
};

/** Compact axis labels (1M, 750K) so dual Y-axes fit without clipping. */
export const formatPlatformTrendAxisCompact = (
  value: number | string,
  language: string
): string => {
  const numeric = Number(value ?? 0);
  if (!Number.isFinite(numeric)) {
    return "";
  }
  return new Intl.NumberFormat(getNumberLocale(language), {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(numeric);
};

/** Reserve gutter space so dual Y-axis ticks sit outside the plot area. */
export const getPlatformTrendChartMargin = (isRTL: boolean) =>
  mirrorChartMargin({ top: 8, right: 14, left: 14, bottom: 4 }, isRTL);

export const PLATFORM_TREND_Y_AXIS = {
  primaryWidth: 58,
  secondaryWidth: 52,
  tickMargin: 6,
} as const;

/** SVG axis ticks — locale digits, anchored outside the plot on each side. */
export const renderPlatformTrendAxisTick =
  (language: string, orientation: "left" | "right") =>
  (props: PlatformTrendAxisTickProps) => {
    const { x = 0, y = 0, payload } = props;
    const label = formatPlatformTrendAxisCompact(payload?.value ?? 0, language);
    const onRight = orientation === "right";

    return (
      <text
        x={x}
        y={y}
        dy={4}
        dx={onRight ? 4 : -4}
        textAnchor={onRight ? "start" : "end"}
        direction="ltr"
        fill="#60718b"
        fontSize={11}
      >
        {label}
      </text>
    );
  };

/** Secondary axis may show smaller whole counts — grouped digits, same anchoring. */
export const renderPlatformTrendSecondaryAxisTick =
  (language: string, orientation: "left" | "right") =>
  (props: PlatformTrendAxisTickProps) => {
    const { x = 0, y = 0, payload } = props;
    const label = formatNumberDigits(Number(payload?.value ?? 0), {}, language);
    const onRight = orientation === "right";

    return (
      <text
        x={x}
        y={y}
        dy={4}
        dx={onRight ? 4 : -4}
        textAnchor={onRight ? "start" : "end"}
        direction="ltr"
        fill="#60718b"
        fontSize={11}
      >
        {label}
      </text>
    );
  };
